// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { Resources } from '@shared/localizations/Resources';
import { QuickTableEditingInfo } from '../elements/QuickTable/QuickTableMisc';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { OrderType } from '@shared/utils/Trading/OrderType';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { RulesSet } from '@shared/utils/Rules/RulesSet';
import { DynProperty } from '@shared/commons/DynProperty';
import { ProfitCalculator } from '@shared/commons/cache/ProfitCalculator';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { SlTpUtils } from '@shared/utils/Trading/SlTpUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { DataCache } from '@shared/commons/DataCache';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { PositionFormatter } from '@shared/commons/cache/Formatters/PositionFormatter';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
import { WDSettings } from '../settings/WDGeneralSettings';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { StringUtils } from '@shared/utils/StringUtils';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
import { TradingLockUtils } from '@shared/utils/TradingLockUtils';
export class PositionItem extends BaseItem {
    constructor(position, sessionSettings, isNetQty) {
        super(sessionSettings);
        this.NetQty = false;
        if (isNullOrUndefined(position)) {
            return;
        }
        this.FPosition = position;
        this.NetQty = !!isNetQty; // #92796 пункт 4 a. ii. доки: https://docs.google.com/document/d/1y4W7_QHbI4siuOD4OwrBucXWxQdbbqJoiw8bfEKvDhI/edit
        this.itemId = PositionItem.GetItemId(position);
        this.Disabled = position.IsPendingExerciseOptionStatus(); // #93127
        this.useInstrumentCurrencyForExposition = false;
        this.userGroupName = null;
        // SuperPosition or its trade (FIFO) #80010
        if (position.SuperPositionId !== '-1') {
            if (position.SuperPositionId !== position.PositionId) {
                this.isSubItem = true;
                const superPos = DataCache.Trading.getPositionById(position.SuperPositionId);
                // от этого сеттинга можно избавиться доставая значение ExerciseOptionStatus в Positions но мне это видится менее правильным
                if (!isNullOrUndefined(superPos)) {
                    this.Disabled = superPos.IsPendingExerciseOptionStatus();
                }
            }
            this.superItemId = position.SuperPositionId;
            const controlType = position.isSuperPosition
                ? DynProperty.EXPAND_SUPER_POSITION_BUTTON_AND_TEXT
                : DynProperty.COMPONENT_OF_SUPER_POSITION_TEXT;
            this.QuickTableEditingInfoMap[0] = (function () {
                return new QuickTableEditingInfo(controlType);
            })();
        }
        this.QuickTableEditingInfoMap[PositionItem.CLOSE_COL_INDEX] = (function () {
            return new QuickTableEditingInfo(DynProperty.CLOSE_BUTTON);
        })();
        this.QuickTableEditingInfoMap[PositionItem.CLOSE_VIA_OE_COL_INDEX] = (function () {
            return new QuickTableEditingInfo(DynProperty.CLOSE_VIA_OE_BUTTON);
        })();
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        switch (column) {
            case PositionItem.SYMBOL_COL_INDEX:
                return colors.tableSymbolForeground;
            case PositionItem.QUANTITY_COL_INDEX:
                return colors.tableQuantityForeground;
            case PositionItem.OPERATION_COL_INDEX:
                if (!isNullOrUndefined(this.FPosition)) {
                    return PositionFormatter.Operation(this.FPosition) === OperationType.Buy
                        ? colors.buyColor
                        : colors.sellColor;
                }
                break;
        }
        return super.getQuickTableCellForeground(column);
    }
    NeedSortMenu() {
        return true;
    }
    GetMenuIndex(headerKey) {
        const idx = PositionItem.ListMenuIndex.indexOf(headerKey);
        return idx === -1 ? 1000 : idx;
    }
    ColumnCount() {
        return PositionItem.columnsParams.length;
    }
    GetColumnParams(column) {
        const result = PositionItem.columnsParams[column];
        let isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        switch (column) {
            case PositionItem.SL_COL_INDEX:
            case PositionItem.TP_COL_INDEX:
            case PositionItem.SLL_COL_INDEX:
                isHidden = IsAllowed.IsProtectiveOrders(); // #108200 doc 4.2
                break;
            case PositionItem.ROUTE_COL_INDEX:
            case PositionItem.TRAILING_STOP_COL_INDEX:
            case PositionItem.USED_MARGIN_COL_INDEX:
            case PositionItem.GROUP_ID_COL_INDEX:
            case PositionItem.USER_GROUP_COL_INDEX:
            case PositionItem.INIT_REQ_COL_INDEX:
            case PositionItem.MA_INIT_REQ_COL_INDEX:
            case PositionItem.MARGIN_SUPLUS_COL_INDEX:
            case PositionItem.PROFIT_USD_INSTRUMENT_CURRENCY_COL_INDEX:
            case PositionItem.COMISSIONS_INSTRUMENT_CURRENCY_COL_INDEX:
            case PositionItem.NET_PL_INSTRUMENT_CURRENCY_COL_INDEX:
            case PositionItem.REALIZED_PNL_COL_INDEX:
            case PositionItem.REALIZED_PNL_INSTRUMENT_CURRENCY_COL_INDEX:
            case PositionItem.DELTA_COL_INDEX:
            case PositionItem.GAMMA_COL_INDEX:
            case PositionItem.VEGA_COL_INDEX:
            case PositionItem.THETA_COL_INDEX:
            case PositionItem.IV_COL_INDEX:
            case PositionItem.RHO_COL_INDEX:
                isHidden = true;
                break;
            case PositionItem.SL_VALUE_COL_INDEX:
                isHidden = !DataCache.isAllowedForMyUser(RulesSet.FUNCTION_SLTP);
                break;
            case PositionItem.PRODUCTTYPE_COL_INDEX:
                isHidden = !RiskPlan.showProductType;
                break;
        }
        result.Hidden = isHidden;
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        return result;
    }
    getColumnData(column) {
        const value = this.getColumnValue(column);
        let FormattedValue = null;
        const position = this.FPosition;
        const account = position.Account;
        const instrument = position.Instrument;
        const ShowLots = WDSettingsUtils.displayAmountInLots();
        switch (column) {
            case PositionItem.QUANTITY_COL_INDEX:
                FormattedValue = DataCache.formatVolume(instrument, Math.abs(value), ShowLots, position.ProductType, account);
                break;
            case PositionItem.BASIS_COL_INDEX:
                FormattedValue = PositionFormatter.PriceStr(position);
                break;
            case PositionItem.EXPDATE_COL_INDEX:
                if (!isNullOrUndefined(value) && value.getTime() > 0) {
                    FormattedValue = DateTimeUtils.FormatToDate(value);
                }
                else {
                    FormattedValue = '';
                }
                break;
            case PositionItem.STRIKE_COL_INDEX:
                FormattedValue = position.StrikePrice > 0 ? instrument.formatPrice(position.StrikePrice) : '';
                break;
            case PositionItem.OPERATION_COL_INDEX:
                FormattedValue = PositionFormatter.OperationStr(position);
                break;
            case PositionItem.DATE_TIME_COL_INDEX:
                FormattedValue = PositionFormatter.DateTimeStr(position);
                break;
            case PositionItem.CUR_PRICE_COL_INDEX:
                FormattedValue = PositionFormatter.CurrentPriceStr(position);
                break;
            case PositionItem.SL_COL_INDEX:
                FormattedValue = PositionFormatter.StopLossPriceStr(position);
                break;
            case PositionItem.TP_COL_INDEX:
                FormattedValue = PositionFormatter.TakeProfitPriceStr(position);
                break;
            case PositionItem.PROFIT_USD_COL_INDEX:
                FormattedValue = position.lastPriceUpdated
                    ? account.formatPrice(position.getGrossPnL(true))
                    : PositionItem.NOT_AVAILABLE;
                break;
            case PositionItem.SWAPS_COL_INDEX:
                FormattedValue = account.formatPrice(position.GetSwaps(true));
                break;
            case PositionItem.EXPOSITION_USD_COL_INDEX:
                if (this.useInstrumentCurrencyForExposition) {
                    const asset = DataCache.GetAssetByName(instrument.Exp2);
                    if (!isNullOrUndefined(asset)) {
                        FormattedValue = asset.formatPrice(position.getExpositionValue(true));
                    }
                    else {
                        FormattedValue = position.getExpositionValue(true) + ' ' + instrument.Exp2;
                    }
                }
                else {
                    FormattedValue = account.formatPrice(position.getExpositionValue(true));
                }
                break;
            case PositionItem.COMISSION_COL_INDEX:
                FormattedValue = PositionFormatter.CommissionStr(position);
                break;
            case PositionItem.TRAILING_STOP_COL_INDEX:
                FormattedValue =
                    !isNullOrUndefined(position.SLOrder) &&
                        !isNaN(position.SLOrder.Price) &&
                        position.SLOrder.OrderType === OrderType.TrailingStop
                        ? instrument.formatPrice(position.SLOrder.TrStopOffset)
                        : '';
                break;
            case PositionItem.USED_MARGIN_COL_INDEX:
                FormattedValue = '';
                break;
            case PositionItem.PL_TICKS_COL_INDEX:
                if (!position.lastPriceUpdated) {
                    FormattedValue = PositionItem.NOT_AVAILABLE;
                }
                else {
                    if (typeof value === 'number') {
                        const visualOffset = SlTpUtils.getDefaultVisualOffsetTypeExcludePercent(instrument);
                        const visualOffsetValue = SlTpUtils.toVisualValue(value, instrument, visualOffset);
                        const visualOffsetStr = SlTpUtils.formatSLTPValue(visualOffsetValue, instrument, visualOffset);
                        FormattedValue =
                            visualOffsetStr +
                                ' ' +
                                Resources.getResource(SlTpUtils.getLocalizationForOffsetMode(visualOffset));
                    }
                    else {
                        FormattedValue = '';
                    }
                }
                break;
            case PositionItem.NET_PL_COL_INDEX:
                FormattedValue = PositionFormatter.NetPnLStr(position);
                break;
            case PositionItem.INIT_REQ_COL_INDEX:
                FormattedValue = account.formatPrice(position.getInitMargin(true));
                break;
            case PositionItem.MA_INIT_REQ_COL_INDEX:
                FormattedValue = account.formatPrice(position.getMaintMargin(true));
                break;
            case PositionItem.MARGIN_SUPLUS_COL_INDEX:
                FormattedValue = account.formatPrice(position.getInitMargin(true) - position.getMaintMargin(true) + position.getNetPnL(true));
                break;
            case PositionItem.POSITION_VALUE_COL_INDEX:
                {
                    const posValue = position.getPositionValue(true);
                    if (isNaN(posValue)) {
                        FormattedValue = PositionItem.NOT_AVAILABLE;
                    }
                    else if (this.useInstrumentCurrencyForExposition) {
                        const asset = DataCache.GetAssetByName(position.Instrument.Exp2);
                        FormattedValue = asset
                            ? asset.formatPrice(posValue)
                            : posValue + ' ' + position.Instrument.Exp2;
                    }
                    else {
                        FormattedValue = account.formatPrice(posValue);
                    }
                }
                break;
            case PositionItem.SL_VALUE_COL_INDEX:
                FormattedValue = account.formatPrice(value);
                break;
            case PositionItem.START_DAY_QTY_COL_INDEX:
                FormattedValue =
                    value !== 0
                        ? DataCache.formatVolume(instrument, Math.abs(value), ShowLots, position.ProductType, account)
                        : '';
                break;
            case PositionItem.LEVERAGE_COL_INDEX:
                {
                    const leverageStr = OrderUtils.GetFormattedLeverage(value, true);
                    FormattedValue = isValidString(leverageStr) ? leverageStr : PositionItem.NOT_AVAILABLE;
                }
                break;
            case PositionItem.ACCRUED_INTEREST_COL_INDEX:
                FormattedValue = value != null ? account.formatPrice(value) : PositionItem.NOT_AVAILABLE;
                break;
            case PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX:
                FormattedValue = !isNullOrUndefined(value)
                    ? PositionFormatter.NetPnLPrecentPosExposureStr(position, value)
                    : PositionItem.NOT_AVAILABLE;
                break;
        }
        if (isNullOrUndefined(FormattedValue)) {
            if (typeof value === 'number') {
                FormattedValue = instrument.formatPrice(value);
            }
            else {
                FormattedValue = !isNullOrUndefined(value) ? value.toString() : '';
            }
        }
        return new ColumnData(value, FormattedValue);
    }
    getColumnValue(column, useAccCurrency = null) {
        const customColumnValues = this.customColumnValues;
        if (column >= PositionItem.columnsParams.length && customColumnValues.hasOwnProperty(column)) {
            return customColumnValues[column];
        }
        const position = this.FPosition;
        const instrument = position.Instrument;
        const account = position.Account;
        const showLots = WDSettingsUtils.displayAmountInLots();
        const UseAccCurrency = useAccCurrency !== null && useAccCurrency !== void 0 ? useAccCurrency : PositionItem.UseAccCurrency(position);
        switch (column) {
            case PositionItem.SYMBOL_COL_INDEX:
                return PositionFormatter.SymbolStr(position);
            case PositionItem.ACCOUNT_COL_INDEX:
                return PositionFormatter.AccountStr(position);
            case PositionItem.QUANTITY_COL_INDEX:
                return PositionItem.GetPositionAmount(position) * PositionItem.GetPositionSign(position, this.NetQty);
            case PositionItem.BASIS_COL_INDEX:
                return PositionFormatter.Price(position);
            case PositionItem.EXPDATE_COL_INDEX:
                if (position.Instrument.ExpDateReal.getFullYear() > 2000) {
                    return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(position.Instrument.ExpDateReal);
                }
                else {
                    return DateTimeUtils._ZeroTime;
                }
            case PositionItem.STRIKE_COL_INDEX:
                return position.StrikePrice > 0 ? position.StrikePrice : 0;
            case PositionItem.POSNUMBER_COL_INDEX:
                return PositionFormatter.PositionId(position);
            case PositionItem.OPERATION_COL_INDEX:
                return PositionFormatter.Operation(position);
            case PositionItem.DATE_TIME_COL_INDEX:
                return PositionFormatter.DateTime(position);
            case PositionItem.CUR_PRICE_COL_INDEX:
                return PositionFormatter.CurrentPrice(position);
            case PositionItem.SL_COL_INDEX:
                return PositionFormatter.StopLossPrice(position);
            case PositionItem.TP_COL_INDEX:
                return PositionFormatter.TakeProfitPrice(position);
            case PositionItem.PROFIT_USD_COL_INDEX:
                return position.lastPriceUpdated ? position.getGrossPnL(UseAccCurrency) : 0;
            case PositionItem.SWAPS_COL_INDEX:
                return position.GetSwaps(UseAccCurrency);
            case PositionItem.EXPOSITION_USD_COL_INDEX:
                return position.getExpositionValue(UseAccCurrency);
            case PositionItem.COMISSION_COL_INDEX:
                return PositionFormatter.Commission(position, UseAccCurrency);
            case PositionItem.COMMENTS_COL_INDEX:
                return position.Comment;
            case PositionItem.ROUTE_COL_INDEX:
                return position.Route;
            case PositionItem.TYPE_COL_INDEX:
                return InstrumentUtils.getInstrumentTypeStringLocalized(instrument.InstrType, instrument.CFD);
            case PositionItem.TRAILING_STOP_COL_INDEX:
                return PositionFormatter.TrStopOffset(position);
            case PositionItem.LOGIN_COL_INDEX:
                return account.userLogin;
            case PositionItem.SYMBOL_DESCRIPTION_COL_INDEX:
                return position.GetInstrumentDescriptionValue();
            case PositionItem.USED_MARGIN_COL_INDEX:
                return undefined;
            case PositionItem.PL_TICKS_COL_INDEX:
                return position.lastPriceUpdated
                    ? InstrumentUtils.tickValue(instrument, position.CurPriceClose, position.Price, position.BuySell === OperationType.Buy ? 1 : -1)
                    : 0;
            case PositionItem.NET_PL_COL_INDEX:
                return PositionFormatter.NetPnL(position, UseAccCurrency);
            case PositionItem.GROUP_ID_COL_INDEX:
                return '';
            case PositionItem.USER_GROUP_COL_INDEX:
                return isValidString(this.userGroupName) ? this.userGroupName : '';
            case PositionItem.INIT_REQ_COL_INDEX:
                return position.getInitMargin(UseAccCurrency);
            case PositionItem.MA_INIT_REQ_COL_INDEX:
                return position.getMaintMargin(UseAccCurrency);
            case PositionItem.MARGIN_SUPLUS_COL_INDEX:
                return (position.getInitMargin(UseAccCurrency) -
                    position.getMaintMargin(UseAccCurrency) +
                    position.getNetPnL(UseAccCurrency));
            case PositionItem.POSITION_VALUE_COL_INDEX:
                return position.getPositionValue(UseAccCurrency);
            case PositionItem.PROFIT_USD_INSTRUMENT_CURRENCY_COL_INDEX:
                return 0;
            case PositionItem.COMISSIONS_INSTRUMENT_CURRENCY_COL_INDEX:
                return -position.GetCommissionFromSettlement(false);
            case PositionItem.NET_PL_INSTRUMENT_CURRENCY_COL_INDEX:
                return 0;
            case PositionItem.SL_VALUE_COL_INDEX:
                return UseAccCurrency ? this.GetSLValue() : this.GetSLValue() / account.getCrossPrice();
            case PositionItem.REALIZED_PNL_COL_INDEX:
            case PositionItem.REALIZED_PNL_INSTRUMENT_CURRENCY_COL_INDEX:
                return position.RealizedPL;
            case PositionItem.DELTA_COL_INDEX:
                return 0;
            case PositionItem.GAMMA_COL_INDEX:
                return 0;
            case PositionItem.VEGA_COL_INDEX:
                return 0;
            case PositionItem.THETA_COL_INDEX:
                return 0;
            case PositionItem.IV_COL_INDEX:
                return 0;
            case PositionItem.RHO_COL_INDEX:
                return 0;
            case PositionItem.PRODUCTTYPE_COL_INDEX:
                return InstrumentUtils.GetLocalizedProductType(instrument, position.ProductType);
            case PositionItem.AVAILABLE_FOR_SELL_COL_INDEX:
                return DataCache.formatVolume(instrument, position.AmountAvailableForSell * (showLots ? 1 : instrument.getLotSize()), showLots, position.ProductType, account);
            case PositionItem.TRADING_EXCHANGE_COL_INDEX:
                return isValidString(instrument.TradingExchange) ? instrument.TradingExchange : '';
            case PositionItem.SLL_COL_INDEX: {
                const order = position.SLOrder;
                return (order === null || order === void 0 ? void 0 : order.StopLimit) && order.OrderType !== OrderType.TrailingStop ? position.SLOrder.Price : null;
            }
            case PositionItem.START_DAY_QTY_COL_INDEX:
                return (PositionItem.GetPositionStartOfDayAmount(position) *
                    PositionItem.GetPositionSign(position, this.NetQty));
            case PositionItem.ASSET_NAME_COL_INDEX:
                return isValidString(instrument.AssetName) ? instrument.AssetName : '';
            case PositionItem.LEVERAGE_COL_INDEX:
                return !isNullOrUndefined(position.Leverage) ? position.Leverage : '';
            case PositionItem.ACCRUED_INTEREST_COL_INDEX:
                return position.AccruedInterest;
            case PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX:
                return PositionFormatter.NetPnLPrecentPosExposure(position, UseAccCurrency);
            case PositionItem.STRATEGY_ID_COL_INDEX:
                return !isNullOrUndefined(position.StrategyId) ? position.StrategyId : '';
            default:
                return '';
        }
    }
    getFormatTotalValue(column, value, totalCurrencyAfterFiltration = null) {
        let formattedValue = '';
        const pos = this.FPosition;
        const dc = pos.DataCache;
        const asset = dc.GetAssetByName(totalCurrencyAfterFiltration !== null && totalCurrencyAfterFiltration !== void 0 ? totalCurrencyAfterFiltration : dc.userBaseCurrency);
        switch (column) {
            case PositionItem.QUANTITY_COL_INDEX:
                formattedValue = value.toFixed(2);
                break;
            case PositionItem.PROFIT_USD_COL_INDEX:
            case PositionItem.SWAPS_COL_INDEX:
            case PositionItem.EXPOSITION_USD_COL_INDEX:
            case PositionItem.COMISSION_COL_INDEX:
            case PositionItem.NET_PL_COL_INDEX:
            case PositionItem.POSITION_VALUE_COL_INDEX:
                formattedValue = isNullOrUndefined(asset) ? value.toFixed(2) : asset.formatPrice(value);
                break;
            case PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX:
                formattedValue = PriceFormatter.formatPrice(value, 2, false) + ' %';
                break;
            default:
                formattedValue = super.getFormatTotalValue(column, value, totalCurrencyAfterFiltration);
        }
        return formattedValue;
    }
    GetSLValue() {
        let slRiskValue = 0;
        try {
            const pos = this.FPosition;
            if (pos != null) {
                // #32401
                if (pos.SLOrder != null) {
                    const ins = pos.Instrument;
                    const acc = pos.Account;
                    const Qty = pos.Amount;
                    const isBuy = pos.BuySell === OperationType.Buy;
                    const price = pos.Price;
                    const slPrice = pos.SLOrder.Price;
                    const openCrossPrice = pos.OpenCrossPrice;
                    slRiskValue = ProfitCalculator.CalculateSLTP(Qty, slPrice, price, ins, acc, isBuy, openCrossPrice, pos.ProductType);
                }
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
        }
        return slRiskValue;
    }
    GetCurrentAccount() {
        return this.FPosition.Account;
    }
    GetCurrentInstrument() {
        return this.FPosition.Instrument;
    }
    InstrumentName() {
        return this.FPosition.Instrument.GetInteriorID();
    }
    DisplayInstrumentName() {
        return this.FPosition.Instrument.DisplayName();
    }
    isAllowEdit(columnIndex) {
        const acc = this.GetCurrentAccount();
        const ins = this.GetCurrentInstrument();
        switch (columnIndex) {
            case PositionItem.SL_COL_INDEX:
            case PositionItem.SLL_COL_INDEX:
                return (!TradingLockUtils.TradingLock.tradingLocked &&
                    IsAllowed.IsSLTPAllowed(ins, acc, true).Allowed &&
                    !this.FPosition.IsActivated);
            case PositionItem.TP_COL_INDEX:
                return !TradingLockUtils.TradingLock.tradingLocked && IsAllowed.IsSLTPAllowed(ins, acc, false).Allowed;
            case PositionItem.PRODUCTTYPE_COL_INDEX:
                return (!TradingLockUtils.TradingLock.tradingLocked &&
                    IsAllowed.IsPositionProductTypeModifyingAllowedForPosition(this.FPosition).Allowed);
            default:
                return super.isAllowEdit(columnIndex);
        }
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        const dc = this.FPosition.DataCache;
        const asset = dc.GetAssetByName(totalCurrencyAfterFiltration !== null && totalCurrencyAfterFiltration !== void 0 ? totalCurrencyAfterFiltration : dc.userBaseCurrency);
        switch (column) {
            case PositionItem.QUANTITY_COL_INDEX:
                return 2;
            case PositionItem.PROFIT_USD_COL_INDEX:
            case PositionItem.SWAPS_COL_INDEX:
            case PositionItem.EXPOSITION_USD_COL_INDEX:
            case PositionItem.COMISSION_COL_INDEX:
            case PositionItem.NET_PL_COL_INDEX:
            case PositionItem.POSITION_VALUE_COL_INDEX:
                return isNullOrUndefined(asset) ? 2 : asset.Point;
            case PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX:
                return 2;
            default:
                return super.getTotalValuePrecision(column, totalCurrencyAfterFiltration);
        }
    }
    static GetItemId(position) {
        return position.PositionId;
    }
    static UseAccCurrency(position) {
        let useAccCurrency = true;
        if (isNullOrUndefined(position)) {
            return useAccCurrency;
        }
        const asset = position.DataCache.userBaseCurrency;
        const assetAcc = position.Account.BaseCurrency;
        if (asset !== assetAcc) {
            useAccCurrency = false;
        }
        return useAccCurrency;
    }
    static GetPositionAmountText(position) {
        if (isNullOrUndefined(position)) {
            return 0;
        }
        // TODO. fix rounding errors.
        if (WDSettings.isQuantityInLots) {
            return position.Amount;
        }
        else {
            const lotSize = position.Instrument.getLotSize();
            return position.Amount * lotSize;
        }
    }
    static GetPositionSign(position, displayQtyAbsolute = true) {
        let sign = 1;
        if (position.BuySell !== OperationType.Buy && displayQtyAbsolute) {
            sign = -1;
        }
        if (position.Amount < 0 && sign < 0) {
            sign = 1;
        }
        return sign;
    }
    static GetPositionAmount(position) {
        if (position != null) {
            return PositionItem.GetAmount(position.Amount, position.Instrument);
        }
        else {
            return 0;
        }
    }
    static GetPositionStartOfDayAmount(position) {
        if (position != null && position.StartOfDayAmount !== null) {
            PositionItem.GetAmount(position.StartOfDayAmount, position.Instrument);
        }
        else {
            return 0;
        }
    }
    static GetAmount(amount, instrument) {
        if (!WDSettings.isQuantityInLots) {
            return amount * instrument.getLotSize();
        }
        else {
            return amount;
        }
    }
}
PositionItem.NOT_AVAILABLE = StringUtils.NOT_AVAILABLE;
PositionItem.SYMBOL_COL_INDEX = 0;
PositionItem.ACCOUNT_COL_INDEX = 1;
PositionItem.QUANTITY_COL_INDEX = 2;
PositionItem.BASIS_COL_INDEX = 3;
PositionItem.EXPDATE_COL_INDEX = 4;
PositionItem.STRIKE_COL_INDEX = 5;
PositionItem.POSNUMBER_COL_INDEX = 6;
PositionItem.OPERATION_COL_INDEX = 7;
PositionItem.DATE_TIME_COL_INDEX = 8;
PositionItem.CUR_PRICE_COL_INDEX = 9;
PositionItem.SL_COL_INDEX = 10;
PositionItem.TP_COL_INDEX = 11;
PositionItem.PROFIT_USD_COL_INDEX = 12;
PositionItem.SWAPS_COL_INDEX = 13;
PositionItem.EXPOSITION_USD_COL_INDEX = 14;
PositionItem.COMISSION_COL_INDEX = 15;
PositionItem.COMMENTS_COL_INDEX = 16;
PositionItem.ROUTE_COL_INDEX = 17;
PositionItem.TYPE_COL_INDEX = 18;
PositionItem.TRAILING_STOP_COL_INDEX = 19;
PositionItem.LOGIN_COL_INDEX = 20;
PositionItem.SYMBOL_DESCRIPTION_COL_INDEX = 21;
PositionItem.USED_MARGIN_COL_INDEX = 22;
PositionItem.PL_TICKS_COL_INDEX = 23;
PositionItem.NET_PL_COL_INDEX = 24;
PositionItem.GROUP_ID_COL_INDEX = 25;
PositionItem.USER_GROUP_COL_INDEX = 26;
PositionItem.INIT_REQ_COL_INDEX = 27;
PositionItem.MA_INIT_REQ_COL_INDEX = 28;
PositionItem.MARGIN_SUPLUS_COL_INDEX = 29;
PositionItem.POSITION_VALUE_COL_INDEX = 30;
PositionItem.PROFIT_USD_INSTRUMENT_CURRENCY_COL_INDEX = 31;
PositionItem.COMISSIONS_INSTRUMENT_CURRENCY_COL_INDEX = 32;
PositionItem.NET_PL_INSTRUMENT_CURRENCY_COL_INDEX = 33;
PositionItem.SL_VALUE_COL_INDEX = 34;
PositionItem.REALIZED_PNL_COL_INDEX = 35;
PositionItem.REALIZED_PNL_INSTRUMENT_CURRENCY_COL_INDEX = 36;
PositionItem.DELTA_COL_INDEX = 37;
PositionItem.GAMMA_COL_INDEX = 38;
PositionItem.VEGA_COL_INDEX = 39;
PositionItem.THETA_COL_INDEX = 40;
PositionItem.IV_COL_INDEX = 41;
PositionItem.RHO_COL_INDEX = 42;
PositionItem.PRODUCTTYPE_COL_INDEX = 43;
PositionItem.AVAILABLE_FOR_SELL_COL_INDEX = 44;
PositionItem.CLOSE_COL_INDEX = 45;
PositionItem.TRADING_EXCHANGE_COL_INDEX = 46;
PositionItem.SLL_COL_INDEX = 47;
PositionItem.START_DAY_QTY_COL_INDEX = 48;
PositionItem.ASSET_NAME_COL_INDEX = 49;
PositionItem.LEVERAGE_COL_INDEX = 50;
PositionItem.ACCRUED_INTEREST_COL_INDEX = 51;
PositionItem.NET_PL_PRECENT_POS_EXPOSURE_COL_INDEX = 52;
PositionItem.CLOSE_VIA_OE_COL_INDEX = 53;
PositionItem.STRATEGY_ID_COL_INDEX = 54;
PositionItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.positions.Symbol', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true, false, true),
    new ColumnParams('panel.positions.Account', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true, false),
    new ColumnParams('panel.positions.Qunatity', 70, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.positions.Basis', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.ExpDate', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    /* 5 */ new ColumnParams('panel.positions.strike', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.pos_number', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, false, true),
    new ColumnParams('panel.positions.Operation', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.positions.date_time', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.cur_price', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 10 */ new ColumnParams('panel.positions.SL', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.positions.TP', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.positions.profit_usd', 70, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.positions.swaps', 70, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.positions.exposition_usd', 70, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    /* 15 */ new ColumnParams('panel.positions.commissions', 70, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.positions.comments', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.route', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.type', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.positions.tr', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 20 */ new ColumnParams('panel.positions.Login', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.SymbolDescription', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.UsedMargin', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, false),
    new ColumnParams('panel.positions.PL_ticks', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.Net_PL', 70, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 25 */ new ColumnParams('panel.positions.GroupId', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.UserGroup', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.InitReq', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.MaintReq', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.MarginSuplus', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 30 */ new ColumnParams('panel.positions.PositionValue', 70, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.positions.profit_usd.InstrumentCurrency', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.commissions.InstrumentCurrency', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false, false, true),
    new ColumnParams('panel.positions.Net_PL.InstrumentCurrency', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.SL_Value', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 35 */ new ColumnParams('panel.positions.RealizedPnL', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.RealizedPnL.InstrumentCurrency', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.Delta', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.Gamma', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.Vega', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 40 */ new ColumnParams('panel.positions.Theta', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.IV', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.Rho', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.ProductType', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.positions.AvailableForSell', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 45 */ new ColumnParams('panel.positions.Close', 70, QTAlignment.Center, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.ExchangeTrading', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.SLL', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false, null, null, null),
    new ColumnParams('panel.positions.StartOfDayQty', 70, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.AssetName', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    /* 50 */ new ColumnParams('panel.positions.Leverage', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.AccruedInterest', 70, QTAlignment.Right, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.positions.Net_PL_Percent', 70, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positions.CloseViaOE', 70, QTAlignment.Center, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positions.StrategyId', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false)
    /* 55 */
];
PositionItem.ListMenuIndex = [
    'panel.positions.Account',
    'panel.positions.UserGroup',
    'panel.positions.Login',
    'panel.positions.route',
    'panel.positions.Symbol',
    'panel.positions.SymbolDescription',
    'panel.positions.type',
    'panel.positions.AssetName',
    'panel.positions.pos_number',
    'panel.positions.GroupId',
    'panel.positions.Operation',
    'panel.positions.Basis',
    'panel.positions.cur_price',
    'panel.positions.Qunatity',
    'panel.positions.StartOfDayQty',
    'panel.positions.date_time',
    'panel.positions.profit_usd',
    'panel.positions.profit_usd.InstrumentCurrency',
    'panel.positions.PL_ticks',
    'panel.positions.Net_PL',
    'panel.positions.Net_PL.InstrumentCurrency',
    'panel.positions.Net_PL_Percent',
    'panel.positions.exposition_usd',
    'panel.positions.PositionValue',
    'panel.positions.commissions',
    'panel.positions.commissions.InstrumentCurrency',
    'panel.positions.swaps',
    'panel.positions.ExpDate',
    'panel.positions.strike',
    'panel.positions.SL',
    'panel.positions.TP',
    'panel.positions.SL_Value',
    'panel.positions.SLL',
    // 'panel.positions.UsedMargin',
    // 'panel.positions.InitReq',
    // 'panel.positions.MaintReq',
    // 'panel.positions.MarginSuplus',
    'panel.positions.ExchangeTrading',
    'panel.positions.Leverage',
    'panel.positions.StrategyId',
    'panel.positions.Close',
    'panel.positions.CloseViaOE'
];
